import { useEffect, useRef, useState } from 'react'
import { ApiError } from '../api/client'
import { useAuth } from '../auth/useAuth'
import { useUsers } from '../features/users/api'
import { AttachmentControl } from './AttachmentControl'
import { formatDate } from '../features/plans/api'
import {
  describeChange,
  useAddNote,
  useAssignItem,
  useCompleteItem,
  useItemHistory,
  useItemNotes,
  useReopenItem,
  useSkipItem,
  type ItemKind,
} from '../features/plans/itemApi'

export interface PanelItem {
  id: string
  kind: ItemKind
  title: string
  description: string | null
  status: 'Open' | 'Complete' | 'Skipped'
  requiresNote: boolean
  requiresAttachment: boolean
  dueDate: string | null
  assignedToUserId: string | null
  completedByDisplayName: string | null
  completedAt: string | null
  skipReason: string | null
}

/**
 * Working one item: complete, skip, reopen, assign, annotate, and read its history.
 *
 * Requirements are shown before they are hit, so nobody discovers a blocked
 * completion only after tapping it. The server enforces them regardless.
 */
export function PlanItemPanel({
  eventId,
  item,
  onClose,
}: {
  eventId: string
  item: PanelItem
  onClose: () => void
}) {
  const { can } = useAuth()
  const complete = useCompleteItem(eventId, item.kind, item.id)
  const skip = useSkipItem(eventId, item.kind, item.id)
  const reopen = useReopenItem(eventId, item.kind, item.id)
  const assign = useAssignItem(eventId, item.kind, item.id)
  const addNote = useAddNote(eventId, item.kind, item.id)

  const { data: notes } = useItemNotes(eventId, item.kind, item.id, true)
  const { data: history } = useItemHistory(eventId, item.kind, item.id, true)
  const { data: users } = useUsers()

  const headingRef = useRef<HTMLHeadingElement>(null)
  const [note, setNote] = useState('')
  const [completionNote, setCompletionNote] = useState('')
  const [skipReason, setSkipReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [lastAction, setLastAction] = useState<(() => Promise<unknown>) | null>(null)

  const isOpen = item.status === 'Open'
  const isSaving = complete.isPending || skip.isPending || reopen.isPending

  // Move focus into the panel when a different item is opened, so a keyboard or
  // screen-reader user lands on what they just chose instead of staying in the
  // list they chose it from.
  const openedItemId = item.id
  useEffect(() => {
    if (openedItemId) {
      headingRef.current?.focus()
    }
  }, [openedItemId])

  const run = async (action: () => Promise<unknown>) => {
    setError(null)
    try {
      await action()
      setLastAction(null)
    } catch (err) {
      // The optimistic update has already been rolled back by the mutation, so
      // the screen is honest again; keep the action so it can simply be retried.
      setLastAction(() => action)
      setError(err instanceof ApiError ? err.detail : 'That action could not be completed.')
    }
  }

  return (
    <aside className="item-panel" aria-label={`Work on ${item.title}`}>
      <div className="page-header">
        <h2 ref={headingRef} tabIndex={-1}>
          {item.title}
        </h2>
        <button type="button" onClick={onClose} aria-label="Close">
          ✕
        </button>
      </div>

      {item.description ? <p>{item.description}</p> : null}

      <div className="card-meta">
        {item.dueDate ? <span>Due {formatDate(item.dueDate)}</span> : null}
        <span>{item.status}</span>
        {item.completedByDisplayName && item.status !== 'Open' ? (
          <span>
            {item.status === 'Skipped' ? 'Skipped' : 'Completed'} by {item.completedByDisplayName}
            {item.completedAt ? ` on ${new Date(item.completedAt).toLocaleDateString()}` : ''}
          </span>
        ) : null}
        {item.skipReason ? <span>Skipped: {item.skipReason}</span> : null}
      </div>

      {/* Announced, not merely coloured: the pending state has to be perceivable
          without seeing it. */}
      <p className="visually-hidden" role="status" aria-live="polite">
        {isSaving ? 'Saving your change.' : `This item is ${item.status.toLowerCase()}.`}
      </p>

      {error ? (
        <div role="alert" className="error retry-box">
          <p>{error}</p>
          {lastAction ? (
            <button type="button" onClick={() => void run(lastAction)}>
              Try again
            </button>
          ) : null}
        </div>
      ) : null}

      {isOpen && (item.requiresNote || item.requiresAttachment) ? (
        <p className="notice">
          Before completing this item you must add
          {item.requiresNote ? ' a note' : ''}
          {item.requiresNote && item.requiresAttachment ? ' and' : ''}
          {item.requiresAttachment ? ' an attachment' : ''}.
        </p>
      ) : null}

      {isOpen ? (
        <div className="panel">
          <h3>Complete</h3>
          {item.requiresNote ? (
            <div className="field">
              <label htmlFor="completion-note">Note (required)</label>
              <textarea
                id="completion-note"
                rows={2}
                value={completionNote}
                onChange={(e) => setCompletionNote(e.target.value)}
              />
            </div>
          ) : null}
          <button
            type="button"
            className={complete.isPending ? 'primary pending' : 'primary'}
            disabled={complete.isPending || (item.requiresNote && !completionNote.trim())}
            onClick={() => run(() => complete.mutateAsync(completionNote.trim() || null))}
          >
            {complete.isPending ? 'Saving…' : 'Mark complete'}
          </button>

          <h3>Skip</h3>
          <p className="hint">
            Skipping is the honest option when work does not apply. It needs a reason and counts as
            resolved.
          </p>
          <div className="field">
            <label htmlFor="skip-reason">Reason</label>
            <input
              id="skip-reason"
              value={skipReason}
              onChange={(e) => setSkipReason(e.target.value)}
            />
          </div>
          <button
            type="button"
            className={skip.isPending ? 'pending' : undefined}
            disabled={!skipReason.trim() || skip.isPending}
            onClick={() => run(() => skip.mutateAsync(skipReason.trim()))}
          >
            {skip.isPending ? 'Saving…' : 'Skip this item'}
          </button>
        </div>
      ) : can('Coordinator') ? (
        <div className="panel">
          <button
            type="button"
            disabled={reopen.isPending}
            onClick={() => run(() => reopen.mutateAsync())}
          >
            Reopen
          </button>
          <p className="hint">Reopening clears the completion record but keeps the history.</p>
        </div>
      ) : null}

      {can('Coordinator') ? (
        <div className="panel">
          <h3>Assignment</h3>
          <div className="field">
            <label htmlFor="assignee">Owner</label>
            <select
              id="assignee"
              value={item.assignedToUserId ?? ''}
              onChange={(e) => run(() => assign.mutateAsync(e.target.value || null))}
            >
              <option value="">Nobody</option>
              {(users ?? [])
                .filter((user) => user.isActive)
                .map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.displayName}
                  </option>
                ))}
            </select>
          </div>
        </div>
      ) : null}

      <AttachmentControl
        eventId={eventId}
        kind={item.kind}
        itemId={item.id}
        required={item.requiresAttachment}
      />

      <div className="panel">
        <h3>Notes</h3>
        {notes && notes.length > 0 ? (
          <ul className="note-list">
            {notes.map((entry) => (
              <li key={entry.id}>
                <p>{entry.body}</p>
                <span className="hint">
                  {entry.authorDisplayName} · {new Date(entry.createdAt).toLocaleString()}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="empty">No notes yet.</p>
        )}

        <form
          className="inline-form"
          onSubmit={(event) => {
            event.preventDefault()
            if (!note.trim()) return
            void run(async () => {
              await addNote.mutateAsync(note.trim())
              setNote('')
            })
          }}
        >
          <div className="field">
            <label htmlFor="new-note">Add a note</label>
            <input id="new-note" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <button type="submit">Add</button>
        </form>
      </div>

      <div className="panel">
        <h3>History</h3>
        {history && history.length > 0 ? (
          <ul className="note-list">
            {history.map((entry) => (
              <li key={entry.id}>
                <p>{describeChange(entry)}</p>
                <span className="hint">
                  {entry.actorDisplayName} · {new Date(entry.occurredAt).toLocaleString()}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="empty">Nothing has changed yet.</p>
        )}
      </div>
    </aside>
  )
}
